import { LineOrientationType } from 'types/lineOrientationType';
import { CoordinatePoint } from 'types/point';
import { getLineOrientation } from 'helpers/getLineOrientation';
import { getSnappedToGrid } from 'helpers/snap/snapToGrid';
import { UnreachableCaseError } from 'helpers/UnreachableCaseError';

interface SnapLineToGridResult<T extends CoordinatePoint> {
  p1: T;
  p2: T;
}

export const snapLineToGrid = <T extends CoordinatePoint>(
  first: T, second: T, d: CoordinatePoint, snapDivision: number, alignedWalls: Immutable.List<CoordinatePoint>[] = []
): SnapLineToGridResult<T> => {
  const p1 = { ...first };
  const p2 = { ...second };
  let c = {
    x: (p1.x + p2.x) / 2 + d.x,
    y: (p1.y + p2.y) / 2 + d.y,
  };
  c = getSnappedToGrid(c, snapDivision);

  const lineType = getLineOrientation(p1, p2);
  switch (lineType) {
    case LineOrientationType.HORIZONTAL:
      p1.y = c.y;
      p2.y = c.y;
      break;

    case LineOrientationType.VERTICAL:
      p1.x = c.x;
      p2.x = c.x;
      break;

    case LineOrientationType.DIAGONAL_DOWN:
    case LineOrientationType.DIAGONAL_UP:
      p1.x += d.x;
      p1.y += d.y;

      p2.x += d.x;
      p2.y += d.y;
      break;

    default: throw new UnreachableCaseError(lineType);
  }

  // if there are any alignments, snap to the alignments as well
  if (alignedWalls.length > 0) {
    for (let i = 0; i < alignedWalls.length; i++) {
      const wallStart = alignedWalls[i].first();
      const wallEnd = alignedWalls[i].last();
      const alignedWallType = wallStart && wallEnd ? getLineOrientation(wallStart, wallEnd) : undefined;

      if (wallEnd && lineType === alignedWallType) {
        if (lineType === LineOrientationType.HORIZONTAL) {
          // Horizontal Alignments align on y axis
          p1.y = wallEnd.y;
          p2.y = wallEnd.y;
        } else { // LineOrientationType.VERTICAL
          // Vertical Alignments align on x axis
          p1.x = wallEnd.x;
          p2.x = wallEnd.x;
        }
      }
    }
  }

  // return the snapped coordinants.
  return {
    p1,
    p2,
  };
};
